import * as React from "react"
import { motion } from "motion/react"
import coinImg from "@/public/coin.png"
import moneyImg from "@/public/money.png"

interface FloatingElementProps {
  children: React.ReactNode
  delay?: number
  duration?: number
  x?: number
  y?: number
  rotate?: number
  scale?: number
  className?: string
}

function FloatingElement({
  children,
  delay = 0,
  duration = 4,
  x = 0,
  y = 0,
  rotate = 0,
  scale = 1,
  className = "",
}: FloatingElementProps) {
  return (
    <motion.div
      className={`absolute pointer-events-none ${className}`}
      style={{ left: `${x}%`, top: `${y}%` }}
      initial={{ opacity: 0, scale: 0, rotate: rotate - 20 }}
      animate={{
        opacity: 0.6,
        scale: scale,
        rotate: [rotate - 5, rotate + 5, rotate - 5],
        y: [0, -15, 0, -10, 0],
      }}
      transition={{
        opacity: { delay, duration: 0.5 },
        scale: { delay, duration: 0.5, type: "spring" },
        rotate: { delay, duration: duration * 1.5, repeat: Infinity, ease: "easeInOut" },
        y: { delay, duration: duration, repeat: Infinity, ease: "easeInOut" },
      }}
    >
      {children}
    </motion.div>
  )
}

function Sparkle({ x, y, delay = 0, size = 20 }: { x: number; y: number; delay?: number; size?: number }) {
  return (
    <motion.div
      className="absolute pointer-events-none"
      style={{ left: `${x}%`, top: `${y}%` }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{
        opacity: [0, 0.6, 0],
        scale: [0.5, 1.2, 0.5],
        rotate: [0, 180, 360],
      }}
      transition={{
        delay,
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        className="text-[#F37020] drop-shadow-lg"
      >
        <path
          d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z"
          fill="currentColor"
        />
      </svg>
    </motion.div>
  )
}

export function RetroBackground() {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {/* Casino dark gradient background */}
      <div 
        className="absolute inset-0"
        style={{
          background: "linear-gradient(135deg, #121220 0%, #0D0D1A 50%, #121220 100%)",
        }}
      />

      {/* Subtle radial glow */}
      <div
        className="absolute inset-0"
        style={{
          background: "radial-gradient(ellipse at 30% 20%, rgba(243, 112, 32, 0.08) 0%, transparent 50%)",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background: "radial-gradient(ellipse at 70% 80%, rgba(255, 215, 0, 0.05) 0%, transparent 40%)",
        }}
      />

      {/* Vignette overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: "radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 100%)",
        }}
      />

      {/* Floating coins - more subtle */}
      <FloatingElement x={5} y={15} delay={0} duration={5} rotate={-15} scale={0.6}>
        <img src={coinImg} alt="" className="w-12 h-12 sm:w-16 sm:h-16 drop-shadow-lg opacity-40" />
      </FloatingElement>

      <FloatingElement x={85} y={20} delay={0.5} duration={4.5} rotate={10} scale={0.7}>
        <img src={coinImg} alt="" className="w-10 h-10 sm:w-14 sm:h-14 drop-shadow-lg opacity-40" />
      </FloatingElement>

      <FloatingElement x={10} y={70} delay={1} duration={5.5} rotate={20} scale={0.5}>
        <img src={coinImg} alt="" className="w-10 h-10 sm:w-12 sm:h-12 drop-shadow-lg opacity-40" />
      </FloatingElement>

      <FloatingElement x={90} y={75} delay={1.5} duration={4} rotate={-10} scale={0.65}>
        <img src={coinImg} alt="" className="w-12 h-12 sm:w-16 sm:h-16 drop-shadow-lg opacity-40" />
      </FloatingElement>

      {/* Floating money - more subtle */}
      <FloatingElement x={-2} y={40} delay={0.3} duration={6} rotate={-25} scale={0.5}>
        <img src={moneyImg} alt="" className="w-20 h-auto sm:w-28 drop-shadow-lg opacity-30" />
      </FloatingElement>

      <FloatingElement x={75} y={45} delay={0.8} duration={5} rotate={15} scale={0.4}>
        <img src={moneyImg} alt="" className="w-16 h-auto sm:w-24 drop-shadow-lg opacity-30" />
      </FloatingElement>

      {/* Orange sparkles */}
      <Sparkle x={15} y={25} delay={0} size={14} />
      <Sparkle x={80} y={15} delay={0.5} size={18} />
      <Sparkle x={25} y={60} delay={1} size={12} />
      <Sparkle x={70} y={70} delay={1.5} size={16} />
      <Sparkle x={50} y={10} delay={2} size={20} />
    </div>
  )
}

export function RetroBackgroundSimple() {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {/* Casino dark gradient background */}
      <div 
        className="absolute inset-0"
        style={{
          background: "linear-gradient(135deg, #121220 0%, #0D0D1A 50%, #121220 100%)",
        }}
      />

      {/* Subtle vignette */}
      <div
        className="absolute inset-0"
        style={{
          background: "radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.2) 100%)",
        }}
      />
    </div>
  )
}
